import * as React from 'react';
import { cn } from '../../lib/utils';

export interface LandingHeroProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  badge?: string;
  badgeIcon?: React.ReactNode;
  title: React.ReactNode;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  /** Trust-bar items shown below CTAs */
  trustItems?: Array<{ icon: React.ReactNode; label: string }>;
}

export const LandingHero = React.forwardRef<HTMLElement, LandingHeroProps>(
  (
    {
      className,
      badge,
      badgeIcon,
      title,
      description,
      primaryAction,
      secondaryAction,
      trustItems,
      children,
      ...props
    },
    ref
  ) => (
    <section
      ref={ref}
      className={cn(
        'relative overflow-hidden pt-20 pb-24 md:pt-28 md:pb-32',
        className
      )}
      {...props}
    >
      {/* Ambient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-tr from-primary/15 to-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {badge && (
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary mb-6">
            {badgeIcon && <span className="shrink-0">{badgeIcon}</span>}
            {badge}
          </div>
        )}

        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 leading-tight">
          {title}
        </h1>

        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed">
          {description}
        </p>

        {(primaryAction || secondaryAction) && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            {primaryAction}
            {secondaryAction}
          </div>
        )}

        {trustItems && trustItems.length > 0 && (
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-muted-foreground">
            {trustItems.map((item, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {item.icon}
                {item.label}
              </span>
            ))}
          </div>
        )}

        {children}
      </div>
    </section>
  )
);

LandingHero.displayName = 'LandingHero';
